import {
  ContactDetailResponseDto,
  ContactMultiValueItemResponseDto,
  PendingContactChangesetDto,
  updateContactDtoSchema,
} from "@shared/generated/crm";
import {
  buildFacebookLink,
  buildInstagramLink,
  buildLinkedInLink,
  getFacebookHandle,
  getInstagramHandle,
  getLinkedInHandle,
  normalizeSocialHandleFields,
} from "@features/Contacts/components/ContactBriefingHeader/contact-summary-chips-helpers";

const toFieldValue = (value: string | null): string => value ?? "";

const excludeSocialLinks = (
  links: ContactMultiValueItemResponseDto[] | null,
): ContactMultiValueItemResponseDto[] | null => {
  if (!links) return links;
  return links.filter(
    (link) =>
      !getInstagramHandle([link]) &&
      !getLinkedInHandle([link]) &&
      !getFacebookHandle([link]),
  );
};

const toFullNameValue = (
  contact: Pick<
    ContactDetailResponseDto,
    "fullName" | "firstName" | "lastName"
  >,
): string =>
  toFieldValue(contact.fullName) ||
  [contact.firstName, contact.lastName].filter(Boolean).join(" ");

const toMultiValue = (
  values:
    | ContactDetailResponseDto["email"]
    | ContactDetailResponseDto["phone"]
    | ContactDetailResponseDto["links"],
): string => {
  if (!values || values.length === 0) return "";
  return [...values]
    .sort((a, b) => a.order - b.order)
    .map((item) => item.value.trim())
    .filter(Boolean)
    .join(", ");
};

const toPendingMultiValue = (
  values:
    | PendingContactChangesetDto["email"]
    | PendingContactChangesetDto["phone"]
    | PendingContactChangesetDto["links"],
): string => {
  if (!values || values.length === 0) return "";
  return [...values]
    .sort(
      (
        a: ContactMultiValueItemResponseDto,
        b: ContactMultiValueItemResponseDto,
      ) => a.order - b.order,
    )
    .map((item) => item.value.trim())
    .filter(Boolean)
    .join(", ");
};

export type ContactFieldItem = {
  id: ContactFieldId;
  label: string;
  value: string;
  suggestedValue?: string;
  inputType?: "email" | "tel" | "url" | "text";
};

type ContactFieldValues = Record<string, string>;

export const updateContactBodySchema = updateContactDtoSchema.strict();

const toContactValueLabelItems = (value: string) => {
  const items = multiValueToItems(value);
  return items.length > 0 ? items.map((item) => ({ value: item })) : [];
};

export const toUpdateContactBody = (
  fieldValues: ContactFieldValues,
  latestPendingFieldSignalAt?: string,
) => {
  const { instagramHandle, linkedinHandle, facebookHandle } =
    normalizeSocialHandleFields(fieldValues);

  return {
    firstName: fieldValues.firstName ?? "",
    lastName: fieldValues.lastName ?? "",
    fullName: fieldValues.fullName ?? "",
    preferredName: fieldValues.preferredName ?? "",
    company: fieldValues.company ?? "",
    title: fieldValues.jobTitle ?? "",
    timezone: fieldValues.timezone ?? "",
    address: fieldValues.address ?? "",
    city: fieldValues.city ?? "",
    state: fieldValues.state ?? "",
    postalCode: fieldValues.postalCode ?? "",
    email: toContactValueLabelItems(fieldValues.email ?? ""),
    phone: toContactValueLabelItems(fieldValues.phone ?? ""),
    links: [
      ...toContactValueLabelItems(fieldValues.links ?? ""),
      ...(instagramHandle
        ? [{ value: buildInstagramLink(instagramHandle) }]
        : []),
      ...(linkedinHandle ? [{ value: buildLinkedInLink(linkedinHandle) }] : []),
      ...(facebookHandle ? [{ value: buildFacebookLink(facebookHandle) }] : []),
    ],
    ...(latestPendingFieldSignalAt
      ? { latestFieldSignalAt: latestPendingFieldSignalAt }
      : {}),
  };
};

export const CONTACT_FIELD_DEFINITIONS = [
  { id: "firstName", label: "First Name" },
  { id: "lastName", label: "Last Name" },
  { id: "preferredName", label: "Preferred Name" },
  { id: "fullName", label: "Full Name" },
  { id: "company", label: "Company" },
  { id: "jobTitle", label: "Job Title" },
  { id: "address", label: "Address" },
  { id: "city", label: "City" },
  { id: "state", label: "State" },
  { id: "postalCode", label: "Postal Code" },
  { id: "timezone", label: "Timezone" },
  { id: "email", label: "Email", inputType: "email" },
  { id: "phone", label: "Phone", inputType: "tel" },
  { id: "instagramHandle", label: "Instagram" },
  { id: "linkedinHandle", label: "LinkedIn" },
  { id: "facebookHandle", label: "Facebook" },
  { id: "links", label: "Links", inputType: "url" },
] as const satisfies ReadonlyArray<{
  id: string;
  label: string;
  inputType?: "email" | "tel" | "url" | "text";
}>;

export type ContactFieldId = (typeof CONTACT_FIELD_DEFINITIONS)[number]["id"];
export type ContactFieldDefinition = (typeof CONTACT_FIELD_DEFINITIONS)[number];

const fieldValueGetters: Record<
  ContactFieldId,
  (contact: ContactDetailResponseDto) => string
> = {
  firstName: (contact) => toFieldValue(contact.firstName),
  lastName: (contact) => toFieldValue(contact.lastName),
  preferredName: (contact) => toFieldValue(contact.preferredName),
  fullName: toFullNameValue,
  company: (contact) => toFieldValue(contact.company),
  jobTitle: (contact) => toFieldValue(contact.title),
  address: (contact) => toFieldValue(contact.address),
  city: (contact) => toFieldValue(contact.city),
  state: (contact) => toFieldValue(contact.state),
  postalCode: (contact) => toFieldValue(contact.postalCode),
  timezone: (contact) => toFieldValue(contact.timezone),
  email: (contact) => toMultiValue(contact.email),
  phone: (contact) => toMultiValue(contact.phone),
  links: (contact) => toMultiValue(excludeSocialLinks(contact.links)),
  instagramHandle: (contact) => toFieldValue(getInstagramHandle(contact.links)),
  linkedinHandle: (contact) => toFieldValue(getLinkedInHandle(contact.links)),
  facebookHandle: (contact) => toFieldValue(getFacebookHandle(contact.links)),
};

export const buildContactFields = (
  contact: ContactDetailResponseDto,
  pendingChangeset?: PendingContactChangesetDto | null,
): ContactFieldItem[] => {
  const fields: ContactFieldItem[] = CONTACT_FIELD_DEFINITIONS.map(
    (definition) => ({
      ...definition,
      value: fieldValueGetters[definition.id](contact),
    }),
  );

  const pendingSuggestionsById: Record<ContactFieldId, string> = {
    firstName: toFieldValue(pendingChangeset?.firstName ?? null),
    lastName: toFieldValue(pendingChangeset?.lastName ?? null),
    preferredName: toFieldValue(pendingChangeset?.preferredName ?? null),
    fullName: toFieldValue(pendingChangeset?.fullName ?? null),
    company: toFieldValue(pendingChangeset?.company ?? null),
    jobTitle: toFieldValue(pendingChangeset?.title ?? null),
    address: toFieldValue(pendingChangeset?.address ?? null),
    city: toFieldValue(pendingChangeset?.city ?? null),
    state: toFieldValue(pendingChangeset?.state ?? null),
    postalCode: toFieldValue(pendingChangeset?.postalCode ?? null),
    timezone: toFieldValue(pendingChangeset?.timezone ?? null),
    email: toPendingMultiValue(pendingChangeset?.email),
    phone: toPendingMultiValue(pendingChangeset?.phone),
    links: toPendingMultiValue(
      excludeSocialLinks(pendingChangeset?.links ?? null),
    ),
    instagramHandle: toFieldValue(
      getInstagramHandle(pendingChangeset?.links ?? null),
    ),
    linkedinHandle: toFieldValue(
      getLinkedInHandle(pendingChangeset?.links ?? null),
    ),
    facebookHandle: toFieldValue(
      getFacebookHandle(pendingChangeset?.links ?? null),
    ),
  };

  return fields.map((field) => ({
    ...field,
    suggestedValue: pendingSuggestionsById[field.id] || undefined,
  }));
};

export const countPendingSuggestionsAgainstValues = (
  contactFields: ContactFieldItem[],
  fieldValues: ContactFieldValues,
): number => {
  const pendingFieldsWithValueCount = contactFields.reduce((count, field) => {
    const suggestedValue = field.suggestedValue?.trim();
    return suggestedValue ? count + 1 : count;
  }, 0);

  const fieldsMatchingSuggestedValueCount = contactFields.reduce(
    (count, field) => {
      const suggestedValue = field.suggestedValue?.trim();
      if (!suggestedValue) return count;

      const currentValue = (fieldValues[field.id] ?? "").trim();
      return currentValue === suggestedValue ? count + 1 : count;
    },
    0,
  );

  return Math.max(
    pendingFieldsWithValueCount - fieldsMatchingSuggestedValueCount,
    0,
  );
};

export const multiValueToItems = (value: string) => {
  return value.split(",").flatMap((item) => {
    const trimmedItem = item.trim();
    return trimmedItem ? [trimmedItem] : [];
  });
};

export const toSaferExternalHref = (raw: string): string | null => {
  const value = raw.trim();
  if (!value) return null;

  const withProtocol = /:\/\//i.test(value)
    ? value.replace(/^.*:\/\//i, "https://")
    : `https://${value}`;

  try {
    const url = new URL(withProtocol);
    const labels = url.hostname.split(".");
    if (labels.length < 2) return null;
    if (/^\d+$/.test(labels[labels.length - 1])) return null;
    return url.toString();
  } catch {
    return null;
  }
};
